// /admin/feeds — staff CRUD over external feed sources (X handles via
// nitter, RSS URLs). Each source has an active toggle, a poll-interval
// (min 15 min), and an optional keyword regex to filter for relevance.
// The poller in cmd/aieditor walks these on a 60s tick.
//
// "Poll now" button forces an out-of-schedule fetch — useful for
// checking a newly-added source without waiting for the next tick.
// Click a source row to expand recent items pulled from it.

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Source {
    id: string;
    name: string;
    poll_shape:    'rss' | 'nitter' | 'web_scan' | 'json_api';
    extract_shape: 'rss_items' | 'whole_page' | 'llm_incident_split' | 'llm_summarize_one';
    handle: string;
    filter_regex?: string | null;
    active: boolean;
    poll_interval_min: number;
    extras?: Record<string, unknown> | null;
    created_at: string;
    last_polled_at?: string | null;
    last_error?: string | null;
    item_count: number;
}

interface Item {
    id: string;
    source_id: string;
    external_id: string;
    title: string;
    body?: string;
    link?: string;
    published_at?: string;
    fetched_at: string;
    triaged_at?: string;
}

export function AdminFeeds() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [sources, setSources] = useState<Source[]>([]);
    const [forbidden, setForbidden] = useState(false);
    const [err, setErr] = useState('');
    const [busyID, setBusyID] = useState<string>('');
    const [expandedID, setExpandedID] = useState<string>('');
    const [items, setItems] = useState<Record<string, Item[]>>({});

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            const r = await api.get<{ sources: Source[] }>('/api/admin/feeds');
            setSources(r.sources ?? []);
        } catch (e) {
            if (e instanceof ApiError && e.status === 403) { setForbidden(true); return; }
            setErr((e as Error).message);
        }
    }, [api, authed]);
    useEffect(() => { load(); }, [load]);

    async function togglePolling(src: Source) {
        setBusyID(src.id);
        try {
            await api.patch(`/api/admin/feeds/${src.id}`, { active: !src.active });
            await load();
        } catch (e) { setErr((e as Error).message); }
        finally    { setBusyID(''); }
    }

    async function pollNow(src: Source) {
        setBusyID(src.id);
        setErr('');
        try {
            const r = await api.post<{ new_items: number }>(`/api/admin/feeds/${src.id}/poll-now`);
            await load();
            await loadItems(src.id);
            setExpandedID(src.id);
            setErr(`✓ ${src.name}: ${r.new_items} new items`);
        } catch (e) { setErr('Poll failed: ' + (e as Error).message); }
        finally    { setBusyID(''); }
    }

    async function loadItems(sourceID: string) {
        try {
            const r = await api.get<{ items: Item[] }>(`/api/admin/feeds/${sourceID}/items`);
            setItems(m => ({ ...m, [sourceID]: r.items ?? [] }));
        } catch (e) { setErr((e as Error).message); }
    }

    async function toggleExpand(src: Source) {
        if (expandedID === src.id) { setExpandedID(''); return; }
        setExpandedID(src.id);
        if (!items[src.id]) await loadItems(src.id);
    }

    async function remove(src: Source) {
        if (!window.confirm(`Delete "${src.name}" and all its stored items?`)) return;
        setBusyID(src.id);
        try {
            await api.del(`/api/admin/feeds/${src.id}`);
            await load();
        } catch (e) { setErr((e as Error).message); }
        finally    { setBusyID(''); }
    }

    if (!authed) return (
        <div className="prelogin">
            <p>Log in.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (forbidden) return (
        <main className="paper-wrap" style={{ padding: 40 }}>
            <h1>Staff only</h1>
        </main>
    );

    return (
        <main className="paper-wrap" style={{ padding: '20px 24px 60px', maxWidth: 1100 }}>
            <div className="masthead">
                <div className="rule-top" />
                <h1>Feed sources</h1>
                <div className="issue">External sources the newsroom polls · nitter (X) + RSS</div>
                <div className="rule-bottom" />
            </div>
            <nav style={navStyle}>
                <Link to="/admin"           style={navLink}>Overview</Link>
                <Link to="/admin/ai-agents" style={navLink}>AI Agents</Link>
                <Link to="/admin/newsroom"  style={navLink}>Newsroom</Link>
                <Link to="/admin/feeds"     style={navLink}>Feeds</Link>
                <Link to="/admin/map"       style={navLink}>Map</Link>
                <Link to="/admin/claims"    style={navLink}>Claims</Link>
                <Link to="/admin/analytics" style={navLink}>Analytics</Link>
            </nav>

            <NewSourceForm onCreated={load} />

            {err && <p style={{ color: err.startsWith('✓') ? '#2b5e3f' : '#8b1919' }}>{err}</p>}

            <section style={{ marginTop: 20 }}>
                {sources.length === 0 ? (
                    <p style={{ fontStyle: 'italic', color: '#666' }}>
                        No sources yet — add one above. Suggested starters:
                        <code style={inlineCode}>CityofCT</code>,{' '}
                        <code style={inlineCode}>TableMountainNP</code>,{' '}
                        <code style={inlineCode}>WSARWesternCape</code> (all nitter kind).
                    </p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--ink)', textAlign: 'left' }}>
                                <th style={th}>Source</th>
                                <th style={th}>Kind</th>
                                <th style={th}>Interval</th>
                                <th style={th}>Items</th>
                                <th style={th}>Last poll</th>
                                <th style={th} colSpan={4}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sources.map(src => (
                                <Fragment key={src.id}>
                                    <tr style={{ borderBottom: '1px solid #eee', opacity: src.active ? 1 : 0.55 }}>
                                        <td style={td}>
                                            <div style={{ fontWeight: 700 }}>{src.name}</div>
                                            <div style={{ color: '#666', fontFamily: 'monospace', fontSize: 11 }}>
                                                {src.poll_shape === 'nitter' ? `@${src.handle}` : src.handle}
                                            </div>
                                            {src.filter_regex && (
                                                <div style={{ color: '#8b1919', fontSize: 11, fontStyle: 'italic' }}>
                                                    filter: <code>{src.filter_regex}</code>
                                                </div>
                                            )}
                                        </td>
                                        <td style={td}>{src.poll_shape}</td>
                                        <td style={td}>{src.poll_interval_min} min</td>
                                        <td style={{ ...td, cursor: 'pointer', textDecoration: 'underline dotted' }}
                                            onClick={() => toggleExpand(src)}
                                            role="button" tabIndex={0}>
                                            {src.item_count}
                                        </td>
                                        <td style={td}>
                                            {src.last_polled_at
                                                ? <span title={src.last_polled_at}>{timeAgo(src.last_polled_at)}</span>
                                                : <span style={{ color: '#999' }}>never</span>}
                                            {src.last_error && (
                                                <div style={{ color: '#8b1919', fontSize: 11 }} title={src.last_error}>⚠ error</div>
                                            )}
                                        </td>
                                        <td style={td}>
                                            <button type="button" onClick={() => pollNow(src)}
                                                    disabled={busyID === src.id} style={btnGhost}>Poll now</button>
                                        </td>
                                        <td style={td}>
                                            <button type="button" onClick={() => togglePolling(src)}
                                                    disabled={busyID === src.id} style={btnGhost}>
                                                {src.active ? 'pause' : 'resume'}
                                            </button>
                                        </td>
                                        <td style={td}>
                                            <button type="button" onClick={() => remove(src)}
                                                    disabled={busyID === src.id}
                                                    style={{ ...btnGhost, color: '#8b1919', borderColor: '#8b1919' }}>
                                                delete
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedID === src.id && (
                                        <tr>
                                            <td colSpan={9} style={{ padding: '12px 16px', background: '#faf8f2', borderBottom: '1px solid #eee' }}>
                                                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                                                    Recent items ({items[src.id]?.length ?? 0}):
                                                </div>
                                                {(items[src.id] ?? []).map(it => (
                                                    <div key={it.id} style={{ padding: '4px 0', borderBottom: '1px dotted #cfc9b7' }}>
                                                        <div style={{ fontWeight: 500 }}>{it.title}</div>
                                                        <div style={{ fontSize: 11, color: '#666' }}>
                                                            {it.published_at ? timeAgo(it.published_at) : 'no date'}
                                                            {it.link && <> · <a href={it.link} target="_blank" rel="noreferrer">source ↗</a></>}
                                                        </div>
                                                    </div>
                                                ))}
                                                {(items[src.id] ?? []).length === 0 && (
                                                    <div style={{ fontStyle: 'italic', color: '#999' }}>No items pulled yet — click "Poll now".</div>
                                                )}
                                                {src.poll_shape === 'nitter' && (
                                                    <NitterHostEditor src={src} onSaved={load} />
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </main>
    );
}

// Default host in the frontend mirrors internal/feeds/shapes.go's
// defaultNitterHost. Nitter mirrors go up and down, so the editor may
// need to override this per-source. Empty input = fall back to default.
const DEFAULT_NITTER_HOST = 'nitter.cf';

function NitterHostEditor({ src, onSaved }: { src: Source; onSaved: () => void }) {
    const api = useApi();
    const current = typeof src.extras?.nitter_host === 'string' ? src.extras.nitter_host as string : '';
    const [val, setVal]         = useState(current);
    const [saving, setSaving]   = useState(false);
    const [msg,    setMsg]      = useState('');

    async function save() {
        setSaving(true);
        setMsg('');
        try {
            await api.patch(`/api/admin/feeds/${src.id}`, { nitter_host: val });
            setMsg('✓ saved');
            onSaved();
        } catch (e) { setMsg((e as Error).message); }
        finally    { setSaving(false); }
    }

    return (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dotted #cfc9b7' }}>
            <div style={{ ...label, marginBottom: 4 }}>Nitter mirror</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                    type="text"
                    value={val}
                    onChange={e => setVal(e.target.value)}
                    placeholder={`${DEFAULT_NITTER_HOST} (default)`}
                    style={{ ...input, maxWidth: 320 }}
                />
                <button type="button" onClick={save} disabled={saving || val === current} style={btnGhost}>
                    {saving ? 'saving…' : 'save'}
                </button>
                {current && (
                    <button type="button" onClick={() => { setVal(''); }} style={{ ...btnGhost, opacity: 0.7 }}>
                        clear
                    </button>
                )}
                {msg && <span style={{ fontSize: 11, color: msg.startsWith('✓') ? '#2b5e3f' : '#8b1919' }}>{msg}</span>}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                Override the fleet-wide default. Leave blank to use <code>{DEFAULT_NITTER_HOST}</code>. Nitter mirrors die often — swap here without a redeploy.
            </div>
        </div>
    );
}

function NewSourceForm({ onCreated }: { onCreated: () => void }) {
    const api = useApi();
    const [pollShape, setPollShape] = useState<'nitter'|'rss'>('nitter');
    const [name, setName] = useState('');
    const [handle, setHandle] = useState('');
    const [interval, setInterval] = useState(30);
    const [filter, setFilter] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    async function submit() {
        setBusy(true); setErr('');
        try {
            // Both rss + nitter shapes use the rss_items extractor. web_scan
            // + json_api sources need to be created via SQL / a richer form
            // (see docs/issues.md T4.1).
            await api.post('/api/admin/feeds', {
                name: name || handle,
                poll_shape:    pollShape,
                extract_shape: 'rss_items',
                handle,
                filter_regex: filter || null,
                poll_interval_min: interval,
            });
            setName(''); setHandle(''); setFilter(''); setInterval(30);
            await onCreated();
        } catch (e) { setErr((e as Error).message); }
        finally    { setBusy(false); }
    }

    return (
        <section style={{ padding: 12, background: '#faf8f2', border: '1px solid var(--line)', marginTop: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 90px', gap: 8, alignItems: 'end' }}>
                <div>
                    <div style={label}>Kind</div>
                    <select value={pollShape} onChange={e => setPollShape(e.target.value as 'nitter'|'rss')} style={input}>
                        <option value="nitter">X (nitter)</option>
                        <option value="rss">RSS URL</option>
                    </select>
                </div>
                <div>
                    <div style={label}>Handle / URL</div>
                    <input type="text" value={handle} onChange={e => setHandle(e.target.value)}
                           placeholder={pollShape === 'nitter' ? 'CityofCT (no @)' : 'https://…/feed'} style={input} />
                </div>
                <div>
                    <div style={label}>Name (display)</div>
                    <input type="text" value={name} onChange={e => setName(e.target.value)}
                           placeholder="City of Cape Town" style={input} />
                </div>
                <div>
                    <div style={label}>Poll (min)</div>
                    <input type="number" min={15} max={1440} value={interval} onChange={e => setInterval(parseInt(e.target.value) || 30)} style={input} />
                </div>
            </div>
            <div style={{ marginTop: 8 }}>
                <div style={label}>Keyword filter (regex, optional — case-insensitive)</div>
                <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
                       placeholder="e.g. vredehoek|table mountain|kloof|load ?shed"
                       style={{ ...input, fontFamily: 'monospace' }} />
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                <button type="button" onClick={submit} disabled={busy || !handle}
                        style={{ ...btnGhost, background: 'var(--ink)', color: '#fff', border: 0 }}>
                    {busy ? 'Adding…' : 'Add source'}
                </button>
                {err && <span style={{ color: '#8b1919', fontSize: 12 }}>{err}</span>}
            </div>
        </section>
    );
}

function timeAgo(iso: string): string {
    const then = new Date(iso).getTime();
    const mins = Math.floor((Date.now() - then) / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const navStyle: React.CSSProperties = { display: 'flex', gap: 16, padding: '12px 0 20px', fontFamily: '-apple-system, sans-serif', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5 };
const navLink: React.CSSProperties = { color: 'var(--ink)', textDecoration: 'none', paddingBottom: 4, borderBottom: '2px solid transparent' };
const th: React.CSSProperties = { padding: '8px 8px', fontFamily: '-apple-system, sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)' };
const td: React.CSSProperties = { padding: '10px 8px', verticalAlign: 'top' };
const label: React.CSSProperties = { fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: 'var(--muted)', fontFamily: '-apple-system, sans-serif', marginBottom: 2 };
const input: React.CSSProperties = { padding: '6px 10px', border: '1px solid var(--line)', background: '#fff', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const btnGhost: React.CSSProperties = { padding: '4px 10px', background: 'transparent', border: '1px solid var(--line)', fontSize: 11, cursor: 'pointer', fontFamily: '-apple-system, sans-serif', textTransform: 'uppercase', letterSpacing: 1 };
const inlineCode: React.CSSProperties = { background: '#fff', border: '1px solid var(--line)', padding: '1px 6px', fontFamily: 'monospace', fontSize: 12 };
